import React, { useState } from 'react';
import CustomModal from '../components-library/CustomModal';
import { MODAL_SIZES } from '../services/exports/Constants';
import OtpFlow from '../components-library/auth/OtpFlow';
import CustomButton from '../components-library/buttons/CustomButton';
import { useTranslation } from 'react-i18next';
import useAppState from '../hooks/global/useAppState';
import useAppMethods from '../hooks/utility/useAppMethods';
import toast from 'react-hot-toast';
import useScreenType from '../hooks/utility/useScreenType';
import CloseIcon from '../assets/icons/actions/CloseIcon';
import Theme from '../components-library/Theme';
import useMobileApp from '../hooks/mobile-app/useMobileApp';

export const OTP_VERIFICATION_PHONE_NUMBER_HEADER = 'x-otp-check-phone-number';
export const OTP_VERIFICATION_PASSWORD_HEADER = 'x-otp-check-password';

export default function OtpVerificationModal() {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OtpVerificationModal' });

  const { phoneNumber, onSubmit, onCancel } = useAppState().otpVerificationModalProps;
  const { toggleOtpVerificationModal } = useAppMethods();
  const { isMobile } = useScreenType();
  const { isMobileApp } = useMobileApp();

  const [otp, setOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  // Track whether the modal close was the result of a successful submit, so
  // the dismiss handler doesn't also fire onCancel (which would settle the
  // outer deferred promise twice).
  const submittedRef = React.useRef(false);

  const close = () => {
    if (!submittedRef.current) {
      onCancel?.();
    }

    toggleOtpVerificationModal();
  };

  const _onSubmit = async () => {
    setLoading(true);
    // `onSubmit` resolves the moment the order endpoint clears the OTP gate
    // (status != 400 OTP_REQUIRED) — see useOrderCheckout/useGiftCardCheckout
    // `onBadRequestError`. The downstream confirm chain (wallet, 3DS, etc.)
    // continues independently after this resolves.
    const accepted = await onSubmit(otp);
    setLoading(false);

    if (!accepted) {
      // Reset the input so the user can retry without manually clearing.
      setOtp(null);

      return toast.error(t('toasts.invalid_code'));
    }

    submittedRef.current = true;

    return toggleOtpVerificationModal();
  };

  return (
    // No `onClose` — backdrop tap must not dismiss this modal. The risk
    // assessor depends on us actually getting an OTP (or an explicit cancel
    // via the close icon below) before continuing the order flow, so an
    // accidental backdrop tap could otherwise drop the user mid-checkout.
    <CustomModal open size={MODAL_SIZES.SM} fullScreen={isMobile}>
      {/*
        A5. E2 has to assert this modal both APPEARS and CLEARS: the risk assessor requires an OTP
        for a fresh guest phone, so it appearing is part of the flow under test, and it clearing is
        what proves the code was accepted. `data-code-requested` distinguishes the phone step from
        the digit-entry step, which otherwise look alike to a locator.
      */}
      <span
        data-test="otp-verification-modal"
        data-code-requested={codeRequested ? 'true' : 'false'}
        className="hidden"
      />
      {/*
        Full screen inside the native app's webview, so the close button would sit under the status
        bar. The spacers carry the native insets; the `safe-area-*` classes are `!important`, which is
        why they sit on their own elements instead of overriding this modal's padding.
      */}
      {isMobileApp && <div data-test="otp-safe-area-top" className="safe-area-top w-full" />}
      <div className="flex justify-end p-sm pb-0">
        <button
          type="button"
          onClick={close}
          className="w-9 h-9 bg-white shadow-icon dark:shadow-none rounded-full flex justify-center items-center"
        >
          <CloseIcon color={Theme.color.black.DEFAULT} />
        </button>
      </div>
      <div className="p-md">
        {!codeRequested && (
          <h3 className="mb-md text-center text-md font-bold text-brand-text-primary">{t('header')}</h3>
        )}
        <OtpFlow
          isStatic
          allowChannelSelection
          revealPhoneNumber
          initialPhoneNumber={phoneNumber}
          onPasswordChange={setOtp}
          onValidationStatusChange={(result: boolean) => setIsComplete(result)}
          onCodeRequested={(value?: string) => value && setCodeRequested(true)}
        />
        {codeRequested && (
          <CustomButton
            // The order endpoint's OTP gate is only clearable through this button, and it is NOT the
            // login form's `login-submit-button` — that one belongs to OtpFlow's own login flow and
            // does not exist inside this modal. Without an id of its own an e2e can only reach it by
            // translated title, which is exactly the coupling `data-test` avoids.
            testId="otp-confirm-button"
            title={t('buttons.confirm')}
            loading={loading}
            disabled={!isComplete}
            onClick={_onSubmit}
            className="mt-lg border-t-gray-100"
          />
        )}
      </div>
      {isMobileApp && <div data-test="otp-safe-area-bottom" className="safe-area-bottom w-full" />}
    </CustomModal>
  );
}
